import React, { useState, useEffect } from 'react';
import { useTheme } from '../ThemeContext';

const ONBOARDING_STEPS = [
  {
    icon: '👋',
    title: 'Welcome to Sim Builder',
    body: 'Create interactive, step-by-step software simulations for your learners. Upload screenshots, mark the areas learners interact with, then export a finished simulation they can open in any browser. This quick tour shows you how it all fits together.',
  },
  {
    icon: '🎬',
    title: 'Create a simulation',
    body: 'Click + New Simulation and give it a title. Open it, then upload your screenshots, one for each step in the workflow you are teaching. You can paste straight from the clipboard, and drag screens to reorder them at any time.',
  },
  {
    icon: '🎯',
    title: 'Add hotspots',
    body: 'On each screen, mark the areas learners must interact with. There are three types of hotspot:',
    items: [
      { label: 'Click', desc: 'Learner clicks a specific area to advance.' },
      { label: 'Text input', desc: 'Learner types the correct word or phrase.' },
      { label: 'Number input', desc: 'Learner enters a number, such as a settings value.' },
    ],
    footer: 'Give each hotspot a tooltip (the task instruction), plus success and fail messages. Tick "Requires previous" to force learners to work in a set order.',
  },
  {
    icon: '🏷️',
    title: 'Organise with tags',
    body: 'Use Manage Tags to create colour-coded labels, then assign them to your simulations, for example by software, department, or course. You can filter your dashboard by tag to quickly find related simulations.',
  },
  {
    icon: '📦',
    title: 'Export and share',
    body: 'When your simulation is ready, export it:',
    items: [
      { label: 'HTML', desc: 'A ZIP with everything inside. Open in any browser, no internet needed.' },
      { label: 'SCORM', desc: 'A package for your LMS (Moodle, Canvas, etc.) that reports completion automatically.' },
    ],
    footer: 'You can also bundle several simulations into one Package for a guided, sequential experience.',
  },
  {
    icon: '🚀',
    title: 'You are all set',
    body: 'That is the whole workflow: create, add hotspots, tag, and export. You can reopen this tour any time using the Getting Started button in the footer. Have fun building!',
  },
];

export default function OnboardingWizard({ onClose }) {
  const { colors, common, mode } = useTheme();
  const [step, setStep] = useState(0);
  const total = ONBOARDING_STEPS.length;
  const current = ONBOARDING_STEPS[step];
  const isFirst = step === 0;
  const isLast = step === total - 1;

  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const headingColor = mode === 'dark' ? '#a5b4fc' : '#4338ca';

  return (
    <div style={common.overlay} role="dialog" aria-modal="true" aria-labelledby="onboarding-title"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...common.modal, maxWidth: 520, display: 'flex', flexDirection: 'column' }}>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} aria-label="Skip tour"
            style={{ ...common.btn, ...common.btnGhost, padding: '4px 10px', fontSize: 13 }}>
            Skip
          </button>
        </div>

        <div style={{ padding: '4px 4px 0' }}>
          <div style={{ fontSize: 52, marginBottom: 12, textAlign: 'center' }}>{current.icon}</div>
          <h2 id="onboarding-title" style={{ ...common.modalTitle, marginBottom: 12, textAlign: 'center', color: headingColor }}>
            {current.title}
          </h2>
          <p style={{ color: colors.textPrimary, fontSize: 15, lineHeight: 1.6, margin: 0 }}>
            {current.body}
          </p>
          {current.items && (
            <ul style={{ paddingLeft: 20, margin: '12px 0 0' }}>
              {current.items.map((item, j) => (
                <li key={j} style={{ color: colors.textPrimary, fontSize: 14, lineHeight: 1.6, marginBottom: 4 }}>
                  <span style={{ fontWeight: 700 }}>{item.label}:</span> {item.desc}
                </li>
              ))}
            </ul>
          )}
          {current.footer && (
            <p style={{ color: colors.textSecondary, fontSize: 13.5, lineHeight: 1.6, margin: '12px 0 0' }}>
              {current.footer}
            </p>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, margin: '24px 0 20px' }}>
          {ONBOARDING_STEPS.map((_, i) => (
            <span key={i} style={{
              width: i === step ? 22 : 8, height: 8, borderRadius: 4,
              background: i === step ? colors.indigo : colors.borderColor,
              transition: 'all 0.2s',
            }} />
          ))}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, paddingTop: 16, borderTop: `1px solid ${colors.borderColor}` }}>
          <button style={{ ...common.btn, ...common.btnGhost, visibility: isFirst ? 'hidden' : 'visible' }}
            onClick={() => setStep(s => s - 1)}>
            ← Back
          </button>
          {isLast ? (
            <button style={{ ...common.btn, ...common.btnPrimary }} onClick={onClose}>
              Get started 🚀
            </button>
          ) : (
            <button style={{ ...common.btn, ...common.btnPrimary }} onClick={() => setStep(s => s + 1)}>
              Next →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
